import { API_ENDPOINT } from "./api";
import { authenticatedFetch } from "./auth";

// Shared request helper for the courses API: authenticated JSON in/out,
// throws an Error with the backend's message on non-2xx responses.
const request = async (path, { method = "GET", body } = {}) => {
    const response = await authenticatedFetch(`${API_ENDPOINT}/courses${path}`, {
        method,
        headers: { "Content-Type": "application/json" },
        ...(body !== undefined && { body: JSON.stringify(body) }),
    });

    if (!response.ok) {
        let message = "Request failed";
        let code;
        try {
            const errorData = await response.json();
            message = errorData.error || errorData.detail || JSON.stringify(errorData);
            code = errorData.code;
        } catch (e) {
            // keep generic message when the body is not JSON
        }
        const error = new Error(message);
        error.status = response.status;
        error.code = code;
        throw error;
    }

    if (response.status === 204) return null;
    return await response.json();
};

// Backend error `code`s mapped to i18n keys, so every course error surfaces
// in the user's language instead of the raw English message from the API.
const ERROR_CODE_KEYS = {
    self_join: "courses.cannotJoinOwn",
    course_not_found: "courses.invalidCode",
    duplicate_sentence: "teach.duplicateSentence",
    not_member: "courses.notMember",
};

/** Localized, user-facing message for an error thrown by the helpers above. */
export const courseErrorMessage = (error, t) => {
    const key = error?.code && ERROR_CODE_KEYS[error.code];
    return key ? t(key) : t("errors.generic");
};

// ---------------------------------------------------------------------------
// Public (no account required)
// ---------------------------------------------------------------------------

/**
 * Read-only folders/sentences of a course by its shareable join code.
 * No scores — those are tracked client-side for the logged-out session.
 * (`request` uses authenticatedFetch, which sends no header when logged out.)
 */
export const getPublicCourse = (code) => request(`/public/${code}/`);

/** Attach anonymous practice results (by share_code) to the logged-in user. */
export const claimScores = (shareCodes, code) =>
    request("/claim-scores/", { method: "POST", body: { share_codes: shareCodes, code } });

// Pending anonymous practice results, kept in localStorage so they survive the
// signup redirect: { code, shareCodes: [] }. Claimed on the next successful auth.
const PENDING_SCORES_KEY = "pendingCourseScores";

const readPending = () => {
    try {
        return JSON.parse(localStorage.getItem(PENDING_SCORES_KEY)) || null;
    } catch (e) {
        return null;
    }
};

/** Remember one anonymous result for `code`; resets the list if the code changed. */
export const addPendingScore = (code, shareCode) => {
    if (!shareCode) return;
    const current = readPending();
    const shareCodes = current && current.code === code ? current.shareCodes : [];
    if (!shareCodes.includes(shareCode)) shareCodes.push(shareCode);
    localStorage.setItem(PENDING_SCORES_KEY, JSON.stringify({ code, shareCodes }));
};

export const getPendingScores = () => readPending();

export const clearPendingScores = () => localStorage.removeItem(PENDING_SCORES_KEY);

/**
 * Claim any pending anonymous scores for the now-authenticated user.
 * No-op (returns null) when there is nothing pending. Returns the joined
 * course_id so the caller can redirect there. Never throws.
 */
export const claimPendingScores = async () => {
    const pending = readPending();
    if (!pending || !pending.shareCodes?.length) return null;
    try {
        const result = await claimScores(pending.shareCodes, pending.code);
        clearPendingScores();
        return result.course_id ?? null;
    } catch (e) {
        // Keep the pending data so a later attempt can still claim it.
        return null;
    }
};

// ---------------------------------------------------------------------------
// Student
// ---------------------------------------------------------------------------

/** Join a course via its 6-letter entry code. */
export const joinCourse = (code) => request("/join/", { method: "POST", body: { code } });

/** Courses the current user participates in, each with a progress summary. */
export const getMyCourses = () => request("/mine/");

/** Folder/sentence breakdown of one joined course incl. own per-sentence scores. */
export const getMyCourseDetail = (courseId) => request(`/mine/${courseId}/`);

export const leaveCourse = (courseId) => request(`/mine/${courseId}/leave/`, { method: "POST" });

/**
 * Course-aware next practice sentence: unpracticed course sentences first,
 * random corpus sentence once everything was practiced.
 * Returns {sentence, difficulty, source, course_id?, course_title?, folder?}.
 */
export const getNextPracticeSentence = (language) =>
    request(`/next-sentence/${language}/`);

// ---------------------------------------------------------------------------
// Teacher
// ---------------------------------------------------------------------------

export const getTeachingCourses = () => request("/teach/");

/** Cross-course summary (totals + per-course quick stats) for the teacher dashboard. */
export const getTeachingOverview = () => request("/teach/overview/");

export const createCourse = (title, language) =>
    request("/teach/", { method: "POST", body: { title, language } });

export const getTeachingCourseDetail = (courseId) => request(`/teach/${courseId}/`);

export const deleteCourse = (courseId) =>
    request(`/teach/${courseId}/`, { method: "DELETE" });

export const createFolder = (courseId, name) =>
    request(`/teach/${courseId}/folders/`, { method: "POST", body: { name } });

export const deleteFolder = (courseId, folderId) =>
    request(`/teach/${courseId}/folders/${folderId}/`, { method: "DELETE" });

/** Add several sentences at once, optionally into a folder. */
export const addSentences = (courseId, sentences, folderId = null) =>
    request(`/teach/${courseId}/sentences/`, {
        method: "POST",
        body: { sentences, folder: folderId },
    });

export const deleteSentence = (courseId, sentenceId) =>
    request(`/teach/${courseId}/sentences/${sentenceId}/`, { method: "DELETE" });

/** Aggregated student/sentence performance for the teacher dashboard. */
export const getCourseStats = (courseId) => request(`/teach/${courseId}/stats/`);
